import { createBasePDF, BRAND_COLORS } from '@/utils/pdfConfig';
import { drawHeader } from '@/utils/pdfHeader';
import { drawFooter } from '@/utils/pdfFooter';
import { duiToLetters } from '@/utils/numberToLetters';
import { dateToLetters } from '@/utils/dateToLetters';

import apiClient from '@/api/client';

export function usePromissoryNote() {

  // --- HELPERS PARA TRADUCIR A LETRAS (Puedes moverlos a tu util numberToLetters) ---
  const rateToLetters = (rate) => {
    const num = Number(rate);
    if (isNaN(num)) return '';
    const words = ["CERO", "UNO", "DOS", "TRES", "CUATRO", "CINCO", "SEIS", "SIETE", "OCHO", "NUEVE", "DIEZ", "ONCE", "DOCE", "TRECE", "CATORCE", "QUINCE", "DIECISEIS", "DIECISIETE", "DIECIOCHO", "DIECINUEVE", "VEINTE", "VEINTIUNO", "VEINTIDOS", "VEINTITRES", "VEINTICUATRO", "VEINTICINCO", "VEINTISEIS", "VEINTISIETE", "VEINTIOCHO", "VEINTINUEVE", "TREINTA", "TREINTA Y UNO", "TREINTA Y DOS", "TREINTA Y TRES", "TREINTA Y CUATRO", "TREINTA Y CINCO", "TREINTA Y SEIS", "TREINTA Y SIETE", "TREINTA Y OCHO", "TREINTA Y NUEVE", "CUARENTA", "CUARENTA Y UNO", "CUARENTA Y DOS", "CUARENTA Y TRES", "CUARENTA Y CUATRO", "CUARENTA Y CINCO", "CUARENTA Y SEIS", "CUARENTA Y SIETE", "CUARENTA Y OCHO", "CUARENTA Y NUEVE", "CINCUENTA", "CINCUENTA Y UNO", "CINCUENTA Y DOS", "CINCUENTA Y TRES", "CINCUENTA Y CUATRO", "CINCUENTA Y CINCO", "CINCUENTA Y SEIS", "CINCUENTA Y SIETE", "CINCUENTA Y OCHO", "CINCUENTA Y NUEVE", "SESENTA", "SESENTA Y UNO", "SESENTA Y DOS", "SESENTA Y TRES", "SESENTA Y CUATRO", "SESENTA Y CINCO", "SESENTA Y SEIS", "SESENTA Y SIETE", "SESENTA Y OCHO", "SESENTA Y NUEVE", "SETENTA", "SETENTA Y UNO", "SETENTA Y DOS", "SETENTA Y TRES", "SETENTA Y CUATRO", "SETENTA Y CINCO", "SETENTA Y SEIS", "SETENTA Y SIETE", "SETENTA Y OCHO", "SETENTA Y NUEVE", "OCHENTA", "OCHENTA Y UNO", "OCHENTA Y DOS", "OCHENTA Y TRES", "OCHENTA Y CUATRO", "OCHENTA Y CINCO", "OCHENTA Y SEIS", "OCHENTA Y SIETE", "OCHENTA Y OCHO", "OCHENTA Y NUEVE", "NOVENTA", "NOVENTA Y UNO", "NOVENTA Y DOS", "NOVENTA Y TRES", "NOVENTA Y CUATRO", "NOVENTA Y CINCO", "NOVENTA Y SEIS", "NOVENTA Y SIETE", "NOVENTA Y OCHO", "NOVENTA Y NUEVE", "CIEN"];

    const intPart = Math.floor(num);
    const decPart = Math.round((num - intPart) * 100);

    let text = words[intPart] || String(intPart);
    if (decPart > 0) {
      text += " PUNTO " + (words[decPart] || String(decPart));
    } else if (String(rate).includes('.')) {
      text += " PUNTO CERO";
    }
    return text;
  };


  // Función maestra para Justificar texto con Negritas y Párrafos
  const renderJustifiedText = (doc, text, startX, startY, maxWidth, lineHeight) => {
    let y = startY;
    const paragraphs = text.split('\n');

    paragraphs.forEach(para => {
      if (!para.trim()) {
        y += lineHeight;
        return;
      }

      const words = [];
      const parts = para.split('**');
      parts.forEach((part, index) => {
        const isBold = index % 2 !== 0;
        const splitWords = part.split(' ').filter(w => w !== '');
        splitWords.forEach(w => words.push({ text: w, isBold }));
      });

      let currentLine = [];
      let currentLineWidth = 0;

      const printLine = (line, isLastLine) => {
        if (line.length === 0) return;

        let lineTextWidth = 0;
        line.forEach(w => {
          doc.setFont('helvetica', w.isBold ? 'bold' : 'normal');
          lineTextWidth += doc.getTextWidth(w.text);
        });

        doc.setFont('helvetica', 'normal');
        const defaultSpaceWidth = doc.getTextWidth(' ');
        let spaceWidth = defaultSpaceWidth;

        if (!isLastLine && line.length > 1) {
          spaceWidth = (maxWidth - lineTextWidth) / (line.length - 1);
        }

        let x = startX;
        line.forEach((w, i) => {
          doc.setFont('helvetica', w.isBold ? 'bold' : 'normal');
          doc.text(w.text, x, y);
          x += doc.getTextWidth(w.text);
          if (i < line.length - 1) x += spaceWidth;
        });
        y += lineHeight;
      };

      words.forEach(w => {
        doc.setFont('helvetica', w.isBold ? 'bold' : 'normal');
        const wordWidth = doc.getTextWidth(w.text);
        doc.setFont('helvetica', 'normal');
        const spaceWidth = doc.getTextWidth(' ');

        const widthWithWord = currentLine.length === 0 ? wordWidth : currentLineWidth + spaceWidth + wordWidth;

        if (widthWithWord > maxWidth && currentLine.length > 0) {
          printLine(currentLine, false);
          currentLine = [w];
          currentLineWidth = wordWidth;
        } else {
          currentLine.push(w);
          currentLineWidth = widthWithWord;
        }
      });

      if (currentLine.length > 0) {
        printLine(currentLine, true);
      }
      y += 4;
    });
    return y;
  };

  const generatePromissoryNote = async (dossier) => {
    const { loan, customer, product } = dossier;

    try {
      const configRes = await apiClient.get('/company_config');
      const config = Array.isArray(configRes.data) ? configRes.data[0] : configRes.data;

      const doc = createBasePDF('p', 'letter');
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      const maxWidth = pageWidth - (margin * 2);

      /* Si tenés el proxy del logo habilitado, descomentá esto:
      drawHeader(doc, {
        title: 'TÍTULO VALOR: PAGARÉ',
        subtitle: `REFERENCIA: ${loan.code}`,
        logo: config.logo_url
      });
      */

      let y = 20;

      // Título
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(0,0,0);
      if(product.contract_type==1){
        doc.text('CONTRATO DE CRÉDITO', pageWidth / 2, y, { align: 'center' });
      }else{
        doc.text('PAGARÉ SIN PROTESTO', pageWidth / 2, y, { align: 'center' });
      }

      y += 12;

      // Encabezado
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      doc.text(`POR: $ ${loan.amount}`, margin, y);
      doc.text(`Referencia No: ${loan.code}`, pageWidth - margin, y, { align: 'right' });

      y += 10;

      // --- VARIABLES DINÁMICAS ---
      const currentDateLetters = dateToLetters(loan.init_date); // ej: "primero de mayo, del año dos mil veintiseis"
      const duiLetters = duiToLetters(customer.identity_document);
      const interestRateLetters = rateToLetters(loan.rate);
      const moratoryRate = loan.moratory_rate || 2.5; // Ajusta si la mora la sacás de otro lado
      const moratoryLetters = rateToLetters(moratoryRate);

      // TEXTO LEGAL CON VARIABLES INTERPOLADAS
      const fullContent =
      `Por este PAGARE, sin protesto el día ${currentDateLetters}.-, Yo ` +
      `**${customer.full_name.toUpperCase()}**, con numero de identidad **${duiLetters} (${customer.identity_document})** ` +
      `me obligo a pagar en forma obligatoria a la sociedad de **${config.emitter_name.toUpperCase()}**, que se abrevia **${config.trade_name.toUpperCase()}** ` +
      `La suma de **$ ${loan.amount}**, más intereses sobre saldos mensuales del **${interestRateLetters} (${loan.rate})**, por ciento mensual calculados a partir de la fecha de suscripción del ` +
      `documento y en caso de que no fueren puntualmente cubiertos el capital más los intereses a su vencimiento, pagaré, además; a partir de esta última fecha ` +
      `intereses moratorios del **${moratoryLetters} (${moratoryRate})**, por ciento mensual, el tipo de interés convencional y moratorio será aumentado de acuerdo a las regulaciones que a este respecto emita. ` +
      `La mora en el pago de capital e intereses en la forma estipulada hará caducar el plazo y la obligación se volverá exigible en su totalidad como si fuera ` +
      `del plazo vencido; todo pago lo haré en la misma moneda que recibo en las oficinas de "**${config.trade_name.toUpperCase()}**". El pago se hará en las oficinas ` +
      `situadas en **${config.address.toUpperCase() || 'SANTIAGO DE MARIA'}**.\n\n` +

      `El pago de la cantidad antes mencionada se hará sin deducciones provenientes de impuesto, tasas, costas procesales, retenciones o recargos de cualquier ` +
      `naturaleza actualmente vigentes o futuras los cuales serán cancelados íntegramente por cuenta del suscriptor de este pagaré.\n\n` +

      `Para todos los efectos legales derivados de este pagare, señalo como domicilio especial **DEPARTAMENTO DE SAN MIGUEL, MUNICIPIO DE SAN MIGUEL, DISTRITO DE ` +
      `SAN MIGUEL CENTRO**, a la jurisdicción de cuyos tribunales me someto, y en caso de acción judicial renunciamos al derecho de apelar del decreto de embargo, ` +
      `sentencia de remate y toda otra providencia apelable que se dictare en el juicio ejecutivo mercantil correspondiente o sus incidencias, siendo a cuenta de ` +
      `**${customer.full_name.toUpperCase()}** todos los gastos que “**${config.trade_name.toUpperCase()}**”, hiciere en el cobro de este Pagaré, en cualquier concepto, ` +
      `incluidos los de cancelación y de cobranza judiciales o extrajudiciales, inclusive los llamados personales y aun cuando por regla general no fuere condenado ` +
      `a ello, facultamos a “**${config.trade_name.toUpperCase()}**”, para que designe a la persona depositaria.`;

      // Invocamos el renderizador justificado
      y = renderJustifiedText(doc, fullContent, margin, y, maxWidth, 5.5);

      // Área de Firma
      y += 25;
      if (y > 230) { doc.addPage(); y = 45; drawHeader(doc, { title: 'PAGARÉ (CONTINUACIÓN)' }); }

      if (customer.signature_base64) {
        doc.addImage(customer.signature_base64, 'PNG', (pageWidth / 2) - 25, y - 15, 50, 20);
      }

      doc.setDrawColor(2, 21, 82);
      doc.setLineWidth(0.5);
      doc.line(pageWidth / 2 - 40, y, pageWidth / 2 + 40, y);

      y += 5;
      doc.setFont('helvetica', 'bold');
      doc.text('FIRMA DEL DEUDOR', pageWidth / 2, y, { align: 'center' });

      // Grid de Datos
      y += 12;
      doc.setFontSize(8);
      const address= `${customer.address}, ${customer.street_avenue}, ${customer.department}, ${customer.municipality}`;
      const details = [
        { l: 'Nombre:', v: customer.full_name.toUpperCase() },
        { l: 'Profesión u Oficio:', v: (customer.profession || 'AGENTE DE VENTAS').toUpperCase() },
        { l: 'DUI:', v: customer.identity_document },
        { l: 'Domicilio Actual:', v: (address || 'N/A').toUpperCase() }
      ];

      details.forEach((item, idx) => {
        doc.setFont('helvetica', 'bold');
        doc.text(item.l, margin, y + (idx * 5));
        doc.setFont('helvetica', 'normal');
        doc.text(item.v, margin + 35, y + (idx * 5));
      });

      drawFooter(doc, { companyName: config.trade_name });
      doc.save(`Pagare_${loan.code}.pdf`);

    } catch (error) {
      console.error(error);
    }
  };

  return { generatePromissoryNote };
}